import React, { useState, useCallback } from 'react';
import {
  Box,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import StopIcon from '@mui/icons-material/Stop';

type ExecutionStatus =
  | 'PENDING'
  | 'RUNNING'
  | 'PAUSED'
  | 'COMPLETED'
  | 'FAILED'
  | 'ABORTED'
  | 'REJECTED';

type Props = {
  status: ExecutionStatus | null;
  onStart: () => void;
  onPause: () => void;
  onResume: () => void;
  onAbort: () => void;
  disabled?: boolean;
};

const ExecutionControls: React.FC<Props> = ({
  status,
  onStart,
  onPause,
  onResume,
  onAbort,
  disabled = false
}) => {
  const [abortDialogOpen, setAbortDialogOpen] = useState(false);

  const isIdle =
    !status ||
    status === 'PENDING' ||
    status === 'COMPLETED' ||
    status === 'FAILED' ||
    status === 'ABORTED' ||
    status === 'REJECTED';
  const isRunning = status === 'RUNNING';
  const isPaused = status === 'PAUSED';

  const handleAbortClick = useCallback(() => {
    setAbortDialogOpen(true);
  }, []);

  const handleAbortConfirm = useCallback(() => {
    setAbortDialogOpen(false);
    onAbort();
  }, [onAbort]);

  const handleAbortCancel = useCallback(() => {
    setAbortDialogOpen(false);
  }, []);

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {isIdle && (
          <Tooltip title="Run">
            <IconButton
              size="small"
              color="success"
              onClick={onStart}
              disabled={disabled}
            >
              <PlayArrowIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        {isRunning && (
          <Tooltip title="Pause">
            <IconButton
              size="small"
              color="warning"
              onClick={onPause}
              disabled={disabled}
            >
              <PauseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        {isPaused && (
          <Tooltip title="Resume">
            <IconButton
              size="small"
              color="success"
              onClick={onResume}
              disabled={disabled}
            >
              <PlayArrowIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        {(isRunning || isPaused) && (
          <Tooltip title="Abort">
            <IconButton
              size="small"
              color="error"
              onClick={handleAbortClick}
              disabled={disabled}
            >
              <StopIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Dialog open={abortDialogOpen} onClose={handleAbortCancel} maxWidth="xs">
        <DialogTitle>Abort Execution</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to abort execution? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAbortCancel}>Cancel</Button>
          <Button
            onClick={handleAbortConfirm}
            color="error"
            variant="contained"
          >
            Abort
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ExecutionControls;
